import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Eye, EyeOff, ShieldCheck, AlertCircle } from "lucide-react";

import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { BrandMark } from "@/components/ui/Brand";
import { cn } from "@/lib/cn";

const loginSchema = z.object({
  email: z.string().min(1, "Required").email("Enter a valid email"),
  password: z.string().min(1, "Required").max(256),
});
type LoginForm = z.infer<typeof loginSchema>;

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(values),
      });
      if (res.status === 401) {
        setServerError("Wrong email or password.");
        return;
      }
      if (!res.ok) {
        const text = await res.text();
        setServerError(text || `Sign-in failed (${res.status}).`);
        return;
      }
      await navigate({ to: "/" });
    } catch {
      setServerError("Cannot reach the server. Check your connection.");
    }
  });

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* ── Left: brand panel ── */}
      <aside className="relative hidden lg:flex flex-col justify-between p-10 bg-navy-900 text-white overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "22px 22px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-24 -right-24 size-96 rounded-full bg-accent-500/30 blur-3xl"
        />
        <div
          aria-hidden
          className="absolute -bottom-24 -left-24 size-96 rounded-full bg-navy-700/40 blur-3xl"
        />

        <div className="relative">
          <BrandMark className="text-white [&_span]:text-white [&_span_span]:text-white/60" />
        </div>

        <div className="relative space-y-7 max-w-md">
          <h1 className="text-4xl font-semibold leading-tight tracking-tight">
            Manage your bilties.
            <br />
            <span className="text-accent-400">See your margin.</span>
          </h1>
          <p className="text-base text-white/70 leading-relaxed">
            One screen for every shipment — Party, Truck, Driver, Materials,
            Freight. The number that matters appears as you type.
          </p>
          <ul className="space-y-3 text-sm text-white/80">
            <Bullet>One-screen bilty entry, no wizard</Bullet>
            <Bullet>GSTIN auto-fetch + smart defaults from history</Bullet>
            <Bullet>Live margin per shipment, before save</Bullet>
            <Bullet>Three-copy GR PDF on demand</Bullet>
          </ul>
        </div>

        <div className="relative text-xs text-white/50">
          AI Logistics · Faridabad, Haryana · GSTIN 06DRAPK9794R1ZP
        </div>
      </aside>

      {/* ── Right: form ── */}
      <main className="flex flex-col items-center justify-center px-6 py-10 sm:px-10">
        <div className="w-full max-w-sm">
          <Link to="/login" className="lg:hidden inline-block mb-8">
            <BrandMark />
          </Link>

          <div className="mb-8">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-accent-700 bg-accent-100 px-2.5 py-1 rounded-full">
              <ShieldCheck className="size-3.5" />
              Internal portal
            </span>
            <h2 className="mt-3 text-xl sm:text-2xl font-semibold text-navy-900">Sign in</h2>
            <p className="mt-1 text-sm text-neutral-600">
              Use your AI Logistics email and password.
            </p>
          </div>

          {serverError && (
            <div className="mb-5 flex items-start gap-2.5 rounded-md border border-danger-700/20 bg-danger-100 px-3.5 py-2.5 text-sm text-danger-700">
              <AlertCircle className="size-4 shrink-0 mt-0.5" />
              <span>{serverError}</span>
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Email" required error={errors.email?.message}>
              <Input
                type="email"
                autoComplete="username"
                autoFocus
                placeholder="you@ailogistics.co.in"
                invalid={!!errors.email}
                {...register("email")}
              />
            </Field>

            <Field
              label="Password"
              required
              error={errors.password?.message}
              rightSlot={
                <Link
                  to="/login"
                  className="text-accent-700 hover:text-accent-600 normal-case tracking-normal"
                >
                  Forgot password?
                </Link>
              }
            >
              <Input
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                placeholder="••••••••"
                invalid={!!errors.password}
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="text-neutral-500 hover:text-navy-800 -my-2 -mr-1 inline-flex items-center justify-center size-11 sm:size-9"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                }
                {...register("password")}
              />
            </Field>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className={cn("w-full", isSubmitting && "opacity-80")}
              iconRight={<ArrowRight className="size-4" />}
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs text-neutral-500 leading-relaxed">
            Need access? Ask an admin to invite you. AI Logistics doesn&apos;t allow
            self-sign-up.
          </p>
        </div>
      </main>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-1.5 inline-block size-1.5 rounded-full bg-accent-400 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
